import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import styles from './MessagesByTopic.module.scss';
import { useFetch } from 'hooks/useFetch';
import { dashboardEndpoints } from 'hooks/endpoints';
import ApexCharts from 'apexcharts';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import Select from 'component/atom/forms/select/select/Select';
import Modal from 'component/hoc/modal/Modal';
import TableComponent from 'component/molecules/Table/TableComponent';
import jsPDF from 'jspdf';
import NoData from 'component/Custom/NoData/NoData';
import Loader from 'component/atom/loader/Loader';

const MESSAGES_COLORS = ['#1859F1', '#99D315', '#FFA61A', '#7B35D8', '#18C0C7'];

const MessagesByTopic = ({ id, startDate, endDate }) => {
  const chartRef = useRef(null);
  const [series, setSeries] = useState([{ name: 'Messages', data: [] }]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCategory, setSelectedCategory] = useState({});

  const DEFAULT_HEIGHT = 470;
  const ROW_SLOT = 56;

  const chartHeight = useMemo(() => {
    const n = categories?.length || 0;
    return Math.max(DEFAULT_HEIGHT, n * ROW_SLOT);
  }, [categories]);

  const { data, refetch } = useFetch(
    ['chat-categories', startDate, endDate, id],
    dashboardEndpoints.GetChatCategories(startDate, endDate, id),
    { enabled: false }
  );

  const { data: countries, refetch: refetchCountries } = useFetch(
    ['countries', startDate, endDate, id],
    dashboardEndpoints.getMesssagesByTopicCountries(startDate, endDate),
    { enabled: false },
    id ? { botId: id } : {}
  );

  const {
    data: questionsByCategory,
    refetch: refetchQuestionsByCategory,
    isLoading,
  } = useFetch(
    [
      'questions-by-category',
      startDate,
      endDate,
      id,
      selectedCategory?.categoryId,
    ],
    dashboardEndpoints.getQuestionsByCategory(startDate, endDate),
    { enabled: false },
    {
      ...(id && { botId: id }),
      ...(selectedCategory?.categoryId && {
        categoryId: selectedCategory.categoryId,
      }),
    }
  );

  useEffect(() => {
    refetchQuestionsByCategory();
  }, [selectedCategory]);

  useEffect(() => {
    refetch();
    refetchCountries();
  }, [startDate, endDate]);

  useEffect(() => {
    const rows = data?.data || [];
    const cats = rows.map((r) => r.categoryText);
    const vals = rows.map((r) => r.chatCount ?? 0);
    setCategories(cats);
    setSeries([{ name: 'Messages', data: vals }]);
  }, [data]);

  const valueAnnotations = useMemo(() => {
    if (!series[0]?.data?.length) return [];

    const maxValue = Math.max(...series[0].data);

    return series[0].data.map((val, i) => ({
      x: 1 === series[0]?.data.length ? val : maxValue,
      y: categories[i],
      marker: {
        size: 0,
        strokeWidth: 0,
        fillColor: 'transparent',
        strokeColor: 'transparent',
      },
      label: {
        text: String(val),
        style: {
          background: 'transparent',
          color: '#000',
          fontSize: '14px',
          fontWeight: 600,
        },
        borderColor: 'transparent',
        borderWidth: 0,
        offsetX: 15,
        offsetY: 1 === series[0]?.data.length ? -120 : 15,
        textAnchor: 'start',
      },
    }));
  }, [series, categories]);

  const ellipsize = (s, n) =>
    s && s.length > n ? s.slice(0, n - 1) + '…' : s || '';
  const maxVal = useMemo(() => {
    const arr = series?.[0]?.data || [];
    return arr.length ? Math.max(...arr) : 0;
  }, [series]);

  const chartSettings = useMemo(
    () => ({
      series,
      options: {
        chart: {
          id: 'messages-by-topic-chart',
          type: 'bar',
          height: chartHeight,
          background: '#FFFFFF',
          toolbar: { show: false },
          animations: { enabled: true },
          events: {
            dataPointSelection: (_event, _chartContext, config) => {
              const idx = config?.dataPointIndex ?? -1;
              const rows = data?.data || [];
              const selected = rows[idx];
              if (selected) {
                setSelectedCategory(selected);
                setShowModal(true);
              }
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '60%',
            borderRadius: 8,
            distributed: true,
            dataLabels: { position: 'bottom' },
          },
        },
        colors: MESSAGES_COLORS,
        dataLabels: {
          enabled: true,
          textAnchor: 'center',
          offsetY: 0,
          offsetX: 20,
          style: {
            fontSize: '14px',
            fontWeight: 500,
            colors: ['#fff'],
          },
          formatter: (val, opts) => {
            const label =
              opts?.w?.globals?.labels?.[opts?.dataPointIndex] || '';
            const gridWidth = opts?.w?.globals?.gridWidth || 0;
            const ratio = maxVal ? val / maxVal : 0;
            const barWidth = gridWidth * ratio;
            const padding = 32;
            const charWidth = 7.5;
            const maxChars = Math.max(
              0,
              Math.floor((barWidth - padding) / charWidth)
            );
            if (maxChars < 3) return '';
            return ellipsize(label, Math.min(60, maxChars));
          },
        },
        xaxis: {
          categories,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          show: false,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        grid: {
          show: false,
          padding: { top: 8, right: 120, bottom: 8, left: 0 },
        },
        tooltip: { enabled: true },
        legend: { show: false },
        annotations: {
          position: 'front',
          points: valueAnnotations,
        },
      },
    }),
    [series, categories, valueAnnotations, chartHeight, data, maxVal]
  );

  const toCsv = (rows, headers) => {
    const escapeCell = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      if (
        s.indexOf('"') !== -1 ||
        s.indexOf(',') !== -1 ||
        s.indexOf('\n') !== -1
      ) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const headerLine = headers.map((h) => escapeCell(h.headerName)).join(',');
    const lines = rows.map((r) =>
      headers.map((h) => escapeCell(r[h.fieldName])).join(',')
    );
    return [headerLine].concat(lines).join('\n');
  };

  const handleDownload = () => {
    const rows = (data?.data || []).map((r) => ({
      topic: r.categoryText,
      messages: r.chatCount ?? 0,
    }));
    if (!rows.length) return;

    const headers = [
      { fieldName: 'topic', headerName: 'Topic' },
      { fieldName: 'messages', headerName: 'Messages' },
    ];

    const csv = '\ufeff' + toCsv(rows, headers);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'messages_by_topic.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div ref={chartRef} className={styles['chart-container']}>
      <div className={styles['actions-container']}>
        <div className={styles['title-container']}>
          <span className={styles['title']}>
            Breakdown of Messages by Topic
          </span>
          <Tooltip
            content='Displays the number of user messages about a 
selected topic ( e.g., admissions), filterable by country'
            className={styles['tooltip']}
          >
            <SvgWithBranding type='info' color='#0744E1' width={22} />
          </Tooltip>
        </div>
        <div className={styles['actions']}>
          <Select
            placeholder='All Countries'
            selectClassname={styles['select']}
            classname={styles['select-container']}
            inputClassName={styles['select-input']}
            value={selectedCountry || 'ALL'}
            onChange={(e) => setSelectedCountry(e.target.value)}
            options={[
              { value: 'ALL', label: 'All Countries' },
              ...(countries?.data || []).map((c) => ({
                value: c,
                label: c,
              })),
            ]}
          />
          {series[0]?.data?.length === 0 ? (
            <Tooltip content='No data available to export'>
              <SvgWithBranding
                className={`${styles['download-icon']} ${styles['disabled']}`}
                type='download2'
                onClick={() => {}}
              />
            </Tooltip>
          ) : (
            <SvgWithBranding
              className={`${styles['download-icon']}`}
              type='download2'
              onClick={handleDownload}
            />
          )}
        </div>
      </div>

      {series[0]?.data?.length === 0 ? (
        <NoData minHeight={420} />
      ) : (
        <ReactApexChart
          options={chartSettings.options}
          series={chartSettings.series}
          type='bar'
          height={chartHeight}
        />
      )}

      <Modal
        title={selectedCategory?.categoryText}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contentClassName={styles['modal']}
        classname={styles['modal-container']}
        children={
          isLoading ? (
            <Loader />
          ) : (
            <TableComponent
              rowKey='text'
              tableContainerClassName={styles['table-container']}
              headerData={{
                tableName: 'Application Status',
                hasExport: false,
                hasSearch: false,
                hasDelete: false,
                hasEdit: false,
                hasPaging: true,
                hasCheckbox: false,
                hasUploadCSV: false,
                hasFilter: false,
                hasExport: false,
                isExpandable: true,
                fields: [
                  { fieldName: 'text', headerName: 'Question' },
                  {
                    fieldName: 'country',
                    headerName: 'Country',
                    hasSort: true,
                  },
                  {
                    fieldName: 'dateCreated',
                    headerName: 'Date',
                    isDatetime: true,
                  },
                ],
              }}
              externalData={questionsByCategory?.data || []}
            />
          )
        }
      />
    </div>
  );
};

export default MessagesByTopic;
